import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { TextField } from "@similarweb/ui-components/forms";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { Textarea } from "app/pages/sales-intelligence/common-components/Textarea/Textarea";
import type { IEmployeeLocationFilterDto } from "app/pages/sales-intelligence/domain/contacts/types/filter-values";
import type { SalesloftCadence } from "app/pages/sales-intelligence/modules/salesloft/types/salesloft.types";
import { usePlaybookProfilesQuery } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/queries/usePlaybookProfilesQuery";
import { ProfileAccessLevel } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/dto";
import type { ScoringConfigType } from "app/pages/sales-intelligence/sub-modules/signals/types/scoring-config";

import { getPlaybookContactsCount } from "../../../../constants";
import type { EmailPersonalization } from "../../../../types/playbooks.types";
import { CadenceCard } from "../../../CadenceCard/CadenceCard";

import { PlaybookEmployeeLocationSummary } from "./PlaybookEmployeeLocationSummary";
import { PlaybookSummaryEmployeeProfiles } from "./PlaybookSummaryEmployeeProfiles";
import { PLAYBOOK_SUMMARY_HALF_WIDTH } from "./playbookSummaryLayout";
import { PlaybookSummaryRow } from "./PlaybookSummaryRow";
import { PlaybookSummaryScoreCriteria } from "./PlaybookSummaryScoreCriteria";

const PLACEHOLDER = "—";
const NAME_MAX_LENGTH = 100;

const PLAYBOOK_SUMMARY_ROW = {
    name: "playbook-summary-row-name",
    description: "playbook-summary-row-description",
    scoreCriteria: "playbook-summary-row-score-criteria",
    contactsLimit: "playbook-summary-row-contacts-limit",
    employeeLocation: "playbook-summary-row-employee-location",
    employeeProfiles: "playbook-summary-row-employee-profiles",
    cadence: "playbook-summary-row-cadence",
    matchingCompanies: "playbook-summary-row-matching-companies",
} as const;

type PlaybookSummaryStepProps = {
    name: string;
    description: string;
    onNameChange: (value: string) => void;
    onDescriptionChange: (value: string) => void;
    isSharedWithAccount: boolean;
    scoreCriteriaLabel: string;
    selectedRuleScope?: ScoringConfigType;
    enabledProfileIds: string[];
    contactsCount?: number;
    employeeLocation?: IEmployeeLocationFilterDto;
    selectedCadence?: SalesloftCadence;
    emailPersonalization: EmailPersonalization;
};

export const PlaybookSummaryStep = ({
    name,
    description,
    onNameChange,
    onDescriptionChange,
    isSharedWithAccount,
    scoreCriteriaLabel,
    selectedRuleScope,
    enabledProfileIds,
    contactsCount,
    employeeLocation,
    selectedCadence,
    emailPersonalization,
}: PlaybookSummaryStepProps) => {
    const { translate } = useTranslate();
    const { data: profilesData } = usePlaybookProfilesQuery(isSharedWithAccount);

    const enabledProfiles = useMemo(() => {
        const profilesById = new Map((profilesData?.profiles ?? []).map((profile) => [profile.id, profile]));

        return enabledProfileIds.flatMap((profileId) => {
            const profile = profilesById.get(profileId);

            if (!profile?.name) {
                return [];
            }

            return [
                {
                    profileId,
                    profileName: profile.name,
                    isShared: profile.accessLevel === ProfileAccessLevel.Account,
                },
            ];
        });
    }, [enabledProfileIds, profilesData?.profiles]);

    const personalizationLabel =
        emailPersonalization === "as-is"
            ? translate("si.playbooks.wizard.cadence.personalization.as_is.title")
            : translate("si.playbooks.wizard.cadence.personalization.ai_outreach.title");

    const contactsLimitLabel = `${getPlaybookContactsCount(contactsCount)} ${translate("si.advanced_search.filters.contacts_per_company_filter.label.suffix")}`;

    const hasEmployeeLocation = !!employeeLocation?.codes?.length || !!employeeLocation?.regions?.length;

    return (
        <Stack
            gap="16px"
            data-automation="playbook-wizard-summary-step"
        >
            <Text
                fontVariant="body1"
                color={colorsPalettes.carbon["400"]}
                data-automation="playbook-wizard-summary-step-subtitle"
            >
                {translate("si.playbooks.wizard.summary.subtitle")}
            </Text>

            <Stack
                borderRadius="6px"
                border={`1px solid ${colorsPalettes.carbon["50"]}`}
            >
                <PlaybookSummaryRow
                    label={translate("si.playbooks.wizard.summary.name")}
                    dataAutomation={PLAYBOOK_SUMMARY_ROW.name}
                >
                    <Stack
                        data-automation="playbook-wizard-name-field"
                        width="100%"
                    >
                        <TextField
                            id="playbook-wizard-name-input"
                            maxLength={NAME_MAX_LENGTH}
                            value={name}
                            placeholder={translate("si.playbooks.wizard.details.name.placeholder")}
                            onChange={onNameChange}
                        />
                    </Stack>
                </PlaybookSummaryRow>
                <PlaybookSummaryRow
                    label={translate("si.playbooks.wizard.summary.description")}
                    dataAutomation={PLAYBOOK_SUMMARY_ROW.description}
                >
                    <Stack
                        data-automation="playbook-wizard-description-field"
                        width="100%"
                    >
                        <Textarea
                            id="playbook-wizard-description-textarea"
                            value={description}
                            placeholder={translate("si.playbooks.wizard.details.description.placeholder")}
                            onChange={(value) => onDescriptionChange(value ?? "")}
                            width="fill"
                        />
                    </Stack>
                </PlaybookSummaryRow>
                <PlaybookSummaryRow
                    label={translate("si.playbooks.wizard.summary.score_criteria")}
                    dataAutomation={PLAYBOOK_SUMMARY_ROW.scoreCriteria}
                >
                    <PlaybookSummaryScoreCriteria
                        scope={selectedRuleScope}
                        label={scoreCriteriaLabel}
                    />
                </PlaybookSummaryRow>
                <PlaybookSummaryRow
                    label={translate("si.lead_gen_filters.category.contacts_per_company_filter.name")}
                    dataAutomation={PLAYBOOK_SUMMARY_ROW.contactsLimit}
                >
                    {contactsLimitLabel}
                </PlaybookSummaryRow>
                <PlaybookSummaryRow
                    label={translate("si.lead_gen_filters.employee_location")}
                    dataAutomation={PLAYBOOK_SUMMARY_ROW.employeeLocation}
                >
                    {hasEmployeeLocation ? (
                        <PlaybookEmployeeLocationSummary employeeLocation={employeeLocation} />
                    ) : (
                        PLACEHOLDER
                    )}
                </PlaybookSummaryRow>
                <PlaybookSummaryRow
                    label={translate("si.playbooks.wizard.summary.employee_profiles")}
                    dataAutomation={PLAYBOOK_SUMMARY_ROW.employeeProfiles}
                >
                    <PlaybookSummaryEmployeeProfiles
                        profiles={enabledProfiles}
                        noneEnabledLabel={translate("si.playbooks.wizard.summary.none_enabled")}
                    />
                </PlaybookSummaryRow>
                <PlaybookSummaryRow
                    label={translate("si.playbooks.wizard.summary.cadence")}
                    dataAutomation={PLAYBOOK_SUMMARY_ROW.cadence}
                >
                    {selectedCadence ? (
                        <CadenceCard
                            cadence={selectedCadence}
                            isSelected={false}
                            readOnly
                            maxWidth={PLAYBOOK_SUMMARY_HALF_WIDTH}
                            footer={
                                <Text
                                    fontVariant="body2"
                                    color={colorsPalettes.carbon["400"]}
                                >
                                    {translate("si.playbooks.wizard.summary.email_personalization", {
                                        value: personalizationLabel,
                                    })}
                                </Text>
                            }
                        />
                    ) : (
                        <Text
                            fontVariant="body1"
                            color={colorsPalettes.carbon["400"]}
                        >
                            {PLACEHOLDER}
                        </Text>
                    )}
                </PlaybookSummaryRow>
                <PlaybookSummaryRow
                    label={translate("si.playbooks.wizard.summary.matching_companies")}
                    dataAutomation={PLAYBOOK_SUMMARY_ROW.matchingCompanies}
                >
                    {PLACEHOLDER}
                </PlaybookSummaryRow>
            </Stack>
        </Stack>
    );
};
